'use client';
import { Box, Flex, Image, Text } from '@skin-seoul/design-system';
import type { ComponentProps } from 'react';
import React from 'react';
import { useUser, useUserService, useLoyaltyService } from '@skin-seoul/service';

const PROFILE_BUTTON_TEST_ID = 'profile-button';

type ProfileButtonProps = ComponentProps<typeof Flex>;

export const ProfileButton = ({ ...rest }: ProfileButtonProps) => {
  const { isAuthenticated, isLoading, user: currentUser } = useUser();
  const { useGetUser } = useUserService();
  const { useGetCustomerBalance } = useLoyaltyService();

  const { data: user, isLoading: isLoadingUser } = useGetUser({ enabled: isAuthenticated });
  const { data: balanceData } = useGetCustomerBalance({
    customerId: String(currentUser?.id ?? ''),
    enabled: isAuthenticated && Boolean(currentUser?.id),
  });

  // Get user's first name, limit to 15 characters as per Figma annotation
  const firstName = user?.address?.shipping?.firstName || 'Guest';
  const displayName = firstName.length > 15 ? `${firstName.slice(0, 15)}...` : firstName;

  const points = balanceData?.availablePoints ?? 0;

  if (isLoading || isLoadingUser) {
    return null;
  }

  return (
    <Flex
      alignItems="center"
      cursor="pointer"
      data-testid={PROFILE_BUTTON_TEST_ID}
      display={{ base: 'none', tablet: 'flex' }}
      gap="8px"
      minWidth="fit-content"
      flexShrink={0}
      {...rest}
    >
      {/* Profile Picture */}
      <Box width="26px" height="26px" borderRadius="50%" overflow="hidden" flexShrink={0} position="relative">
        <Image
          src={
            isAuthenticated
              ? 'https://dev-media.skin-seoul.com/profile-logo-images/user-logo-image.png'
              : 'https://dev-media.skin-seoul.com/profile-logo-images/guest-logo-image.png'
          }
          alt="Profile"
          width="100%"
          height="100%"
          objectFit="cover"
        />
      </Box>

      {/* Text Content */}
      <Flex direction="column" gap="2px" alignItems="flex-start">
        {isAuthenticated ? (
          <>
            {/* Logged in state */}
            <Text textStyle="label.small" fontWeight="semibold" color="generic.0" whiteSpace="nowrap">
              Hi, {displayName}
            </Text>
            <Text textStyle="label.xsmall" fontWeight="regular" color="primary.200" whiteSpace="nowrap">
              {points} pts
            </Text>
          </>
        ) : (
          <>
            {/* Not logged in state */}
            <Text textStyle="label.small" fontWeight="semibold" color="generic.0" whiteSpace="nowrap">
              Sign In
            </Text>
            <Text textStyle="label.xsmall" fontWeight="regular" color="neutral.400" whiteSpace="nowrap">
              For free Shipping
            </Text>
          </>
        )}
      </Flex>
    </Flex>
  );
};

export default ProfileButton;
